import { Box, Typography } from "@mui/material";
import Article from "@/src/components/journal-site/common/article/article";
import RankedArticle from "@/src/_types/Article/ranked-article";
import {
  FONT_UI,
  hairlineTop,
} from "@/src/components/common/broadsheet/tokens";

export type RankingMetric = "citations" | "views" | "downloads";

const METRICS: Record<
  RankingMetric,
  {
    field: "citation_count" | "view_count" | "download_count";
    one: string;
    many: string;
    ranking: string;
    empty: string;
  }
> = {
  citations: {
    field: "citation_count",
    one: "citation",
    many: "citations",
    ranking: "Ranked by OpenAlex citations",
    empty: "No citation data is available yet.",
  },
  views: {
    field: "view_count",
    one: "view",
    many: "views",
    ranking: "Ranked by unique views in the last 2 years",
    empty: "No view data is available yet.",
  },
  downloads: {
    field: "download_count",
    one: "download",
    many: "downloads",
    ranking: "Ranked by unique PDF downloads in the last 2 years",
    empty: "No download data is available yet.",
  },
};

export function rankingCaption(metric: RankingMetric) {
  return METRICS[metric].ranking;
}

function countLabel(metric: RankingMetric, count: number) {
  const { one, many } = METRICS[metric];
  return `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;
}

export default function RankedArticleList({
  metric,
  articles,
  showJournal,
}: {
  metric: RankingMetric;
  articles: RankedArticle[];
  showJournal?: boolean;
}) {
  if (articles.length === 0) {
    return (
      <Typography
        component="p"
        sx={{
          m: 0,
          py: 1,
          fontFamily: FONT_UI,
          fontSize: "0.8125rem",
          color: "custom.inkMuted",
          ...hairlineTop,
        }}
      >
        {METRICS[metric].empty}
      </Typography>
    );
  }

  return (
    <>
      {articles.map((article) => (
        <Box key={article.id} sx={{ py: 0.75, ...hairlineTop }}>
          <Article
            article={article}
            meta={[
              <Box
                key="count"
                component="span"
                sx={{ fontWeight: 700, color: "custom.ink" }}
              >
                {countLabel(metric, article[METRICS[metric].field] ?? 0)}
              </Box>,
              showJournal ? article.journal?.title : null,
            ]}
          />
        </Box>
      ))}
    </>
  );
}
